import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RingoverService } from '../../../services/ringover.service';
import { NotificationService } from 'src/app/shared/services/notification.service';

/**
 * Widget réutilisable Ringover : bouton "Appeler" (callback) + panneau SMS,
 * à poser sur n'importe quelle fiche affichant un numéro de téléphone.
 *
 * Exemple :
 *   <app-ringover-click-to-call
 *     [phoneNumber]="contact.phone"
 *     [displayName]="contact.name"
 *     [projectId]="project.project_id">
 *   </app-ringover-click-to-call>
 *
 * Si `projectId` (ou `contactId`) est fourni, le clic sur "Appeler" arme aussi
 * le suivi serveur de l'appel : résultat d'appel OK/NRP + statut lead NRP
 * automatique si le client ne décroche pas (même mécanique que les pages
 * Contacts / Transactions / Agenda).
 */
@Component({
  selector: 'app-ringover-click-to-call',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="rgo-widget" *ngIf="phoneNumber as num">
      <div class="rgo-actions">
        <a
          class="rgo-btn rgo-call"
          [href]="'tel:' + num"
          (click)="onCall(num)"
          [attr.aria-label]="'Appeler ' + (displayName || num)">
          <span class="rgo-ico">📞</span>
        </a>

        <button
          type="button"
          class="rgo-btn rgo-sms"
          [class.active]="smsOpen"
          (click)="smsOpen = !smsOpen"
          [attr.aria-expanded]="smsOpen"
          aria-label="Envoyer un SMS">
          <span class="rgo-sms-txt">SMS</span>
        </button>
      </div>

      <div class="rgo-sms-panel" *ngIf="smsOpen">
        <div class="rgo-sms-to">
          Vers <strong>{{ displayName || num }}</strong>
        </div>
        <textarea
          class="rgo-sms-input"
          [(ngModel)]="smsContent"
          rows="3"
          maxlength="1000"
          placeholder="Votre message…"
          [disabled]="sending"></textarea>
        <div class="rgo-sms-footer">
          <span class="rgo-count">{{ smsContent.length }}/1000</span>
          <div class="rgo-sms-buttons">
            <button type="button" class="rgo-btn rgo-cancel"
              (click)="smsOpen = false" [disabled]="sending">Annuler</button>
            <button type="button" class="rgo-btn rgo-send"
              [disabled]="sending || !smsContent.trim()"
              (click)="onSendSms(num)">
              {{ sending ? 'Envoi…' : 'Envoyer' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .rgo-widget { display: inline-block; font-family: inherit; }
    .rgo-actions { display: flex; gap: 8px; }
    .rgo-btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 6px 10px; border: 1px solid transparent; border-radius: 8px;
      font-size: 14px; font-weight: 600; cursor: pointer;
      transition: background .15s, opacity .15s;
    }
    .rgo-btn:disabled { opacity: .55; cursor: not-allowed; }
    .rgo-ico { font-size: 15px; line-height: 1; }
    .rgo-sms-txt { font-size: 10px; font-weight: 700; letter-spacing: .04em; line-height: 1; }
    .rgo-call { background: #1f9d55; color: #fff; }
    .rgo-call:hover:not(:disabled) { background: #198047; }
    .rgo-sms { background: #eef2ff; color: #3949ab; border-color: #c5cae9; }
    .rgo-sms.active, .rgo-sms:hover:not(:disabled) { background: #e0e7ff; }
    .rgo-sms-panel {
      margin-top: 8px; padding: 10px; border: 1px solid #e0e0e0;
      border-radius: 10px; background: #fafafa; min-width: 260px;
    }
    .rgo-sms-to { font-size: 12px; color: #555; margin-bottom: 6px; }
    .rgo-sms-input {
      width: 100%; box-sizing: border-box; resize: vertical;
      border: 1px solid #cfd8dc; border-radius: 8px; padding: 8px; font: inherit;
    }
    .rgo-sms-footer {
      display: flex; align-items: center; justify-content: space-between; margin-top: 8px;
    }
    .rgo-count { font-size: 11px; color: #888; }
    .rgo-sms-buttons { display: flex; gap: 8px; }
    .rgo-cancel { background: #fff; color: #555; border-color: #ddd; }
    .rgo-send { background: #3949ab; color: #fff; }
    .rgo-send:hover:not(:disabled) { background: #2f3e96; }
  `]
})
export class RingoverClickToCallComponent {
  /** Numéro à appeler / destinataire SMS (E.164 de préférence, ex +33612345678). */
  @Input({ required: true }) phoneNumber!: string;
  /** Libellé affiché (nom du contact). Optionnel. */
  @Input() displayName?: string;
  /** Dossier CAC lié : arme le suivi NRP serveur au clic sur "Appeler". */
  @Input() projectId?: string | null;
  /** Contact CRM lié (alternative à projectId) pour le suivi NRP. */
  @Input() contactId?: number | null;

  sending = false;
  smsOpen = false;
  smsContent = '';

  constructor(
    private ringover: RingoverService,
    private notify: NotificationService
  ) {}

  /** Arme le suivi NRP serveur en parallèle de l'ouverture du lien `tel:`.
   *  Sans projectId/contactId, l'appel part quand même mais sans suivi. */
  onCall(num: string): void {
    if (!this.projectId && this.contactId == null) return;
    this.ringover.trackCall({
      to_number: num,
      ...(this.contactId != null ? { contact_id: this.contactId } : {}),
      ...(this.projectId ? { project_id: this.projectId } : {}),
    }).subscribe({
      next: (res) => {
        if (!res?.calls_read) {
          this.notify.addNotification(
            'warn',
            'Suivi NRP désactivé',
            res?.message || "La clé Ringover n'a pas la permission 'Calls Read'."
          );
        }
      },
      error: (err) => {
        this.notify.addNotification(
          'warn',
          'Suivi NRP indisponible',
          err?.error?.detail || "Le suivi automatique du résultat d'appel n'a pas pu être armé."
        );
      }
    });
  }

  onSendSms(num: string): void {
    const content = this.smsContent.trim();
    if (!content || this.sending) return;
    this.sending = true;
    this.ringover.sendSms({ to_number: num, content }).subscribe({
      next: () => {
        this.sending = false;
        this.smsOpen = false;
        this.smsContent = '';
        this.notify.addNotification('success', 'SMS envoyé', `SMS transmis à ${this.displayName || num}.`);
      },
      error: (err) => {
        this.sending = false;
        this.notify.addNotification('error', 'Échec du SMS', this.errMsg(err));
      }
    });
  }

  private errMsg(err: any): string {
    return err?.error?.detail || err?.message || 'Erreur inconnue. Vérifiez la configuration Ringover.';
  }
}
